import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { endOfMonth, format, startOfMonth } from 'date-fns'
import { State } from 'index'
import { useDispatch, useSelector } from 'react-redux'
import { fetchStoreCalendars } from 'reducks/storeCalendar/operations'
import { getStocks } from 'reducks/stock/selectors'
import { getSchedules } from 'reducks/schedule/selectors'
import { getStoreCalendars } from 'reducks/storeCalendar/selectors'
import { fetchClosedDates } from 'reducks/calendar/operations'
import { updateModalHeader } from 'reducks/modal/operations'
import { getSpecialHolidays } from 'reducks/calendar/selectors'
import { getHolidays } from 'reducks/store/selectors'
import { getStoreRef } from 'reducks/auth/selectors'
import { IndividualScheduleCalendarContainer } from 'components/Calendar'
import { IndividualScheduleEditCard } from 'components/Card'
import { StockType } from 'types/stock'
import { ScheduleType } from 'types/schedule'
import { BreadType } from 'types/bread'
import { DayOfWeekType } from 'lib/daysOfWeek'
import { dateToString, parseToDate } from 'lib/helper'
import { useSetIndividualScheduleDates } from 'hooks/schedule'
import { useSaveIndividualSettings } from 'hooks/stock'

type Props = {
  bread: BreadType
}

const IndividualScheduleModal: FC<Props> = (props) => {
  const { bread } = props
  const type = 'preorder'

  const dispatch = useDispatch()
  const stocks: StockType[] = useSelector((state: State) => getStocks(state))
  const schedules: ScheduleType[] = useSelector((state: State) => getSchedules(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const specialHolidays = useSelector((state: State) => getSpecialHolidays(state))
  const holidays = useSelector((state: State) => getHolidays(state))
  const storeCalendars = useSelector((state: State) => getStoreCalendars(state))

  const [activeDates, setActiveDates] = useState<Date[]>([])
  const [businessDays, setBusinessDays] = useState<DayOfWeekType[]>([])
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date())
  const [individualSettingDates, setIndividualSettingDates] = useState<Date[]>([])
  const [settingDate, setSettingDate] = useState<Date | undefined>(undefined)
  const [stockList, setStockList] = useState<StockType[]>([])

  const schedulesOfBread: ScheduleType[] = useMemo(() => {
    if (!bread) return []
    return schedules.filter((schedule) => schedule.bread_ref.id === bread.bread_ref?.id)
  }, [schedules, bread])

  const handleCurrentMonth = useCallback((month: Date) => setCurrentMonth(month), [setCurrentMonth])
  const selectSettingDate = useCallback(
    (date: Date) => {
      setSettingDate(date)
      dispatch(updateModalHeader(`${format(date, 'M月d日')}の設定を変更`))
    },
    [setSettingDate, dispatch]
  )
  const backToSelectDate = useCallback(() => setSettingDate(undefined), [setSettingDate])
  const saveIndividualSettings = useSaveIndividualSettings(
    backToSelectDate,
    bread,
    settingDate,
    stockList,
    storeRef,
    type
  )

  // 対象商品のstocksをローカルのstateにセット
  useEffect(() => {
    if (!bread) return
    const stocksOfBread = stocks.filter((stock) => stock.bread_ref.id === bread.bread_ref?.id)
    setStockList([...stocksOfBread])
  }, [stocks, bread])

  // 月が変わったら休業日を取得する
  useEffect(() => {
    if (!storeRef) return
    dispatch(
      fetchClosedDates(startOfMonth(currentMonth), endOfMonth(currentMonth), holidays, specialHolidays, storeRef)
    )
  }, [currentMonth, holidays, storeRef, specialHolidays])

  // 月が変わったら臨時営業日を取得する
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchStoreCalendars(storeRef, startOfMonth(currentMonth)))
  }, [currentMonth, storeRef])

  // 事前予約の受渡可能日に設定済みの日付を取得
  useEffect(() => {
    const dates = stockList.map((stock) => parseToDate(stock.date))
    setActiveDates([...dates])
  }, [stockList])

  // 営業曜日を取得する
  useEffect(() => {
    const _businessDays = schedulesOfBread.reduce((prev, schedule) => {
      return Array.from(new Set([...prev, ...schedule.days_of_week]))
    }, [] as DayOfWeekType[])
    setBusinessDays([..._businessDays])
  }, [schedulesOfBread])

  // 個別で設定されている日付かを判定
  useSetIndividualScheduleDates(schedulesOfBread, setIndividualSettingDates, specialHolidays, stockList, storeCalendars)

  return (
    <>
      {typeof settingDate === 'undefined' ? (
        <IndividualScheduleCalendarContainer
          activeDates={activeDates}
          businessDays={businessDays}
          currentMonth={currentMonth}
          handleCurrentMonth={handleCurrentMonth}
          individualSettingDates={individualSettingDates}
          selectSettingDate={selectSettingDate}
        />
      ) : (
        <IndividualScheduleEditCard
          backToSelectDate={backToSelectDate}
          bread={bread}
          date={settingDate}
          saveIndividualSettings={saveIndividualSettings}
          schedules={schedulesOfBread}
          stocks={stockList.filter((stock) => stock.date === dateToString(settingDate))}
          type={type}
        />
      )}
    </>
  )
}

export default IndividualScheduleModal
